/**
 * RegistrationTracker (C-1b) — new-registration workflow tracker.
 * Stage KPI cards + filters + table + "New registration" dialog.
 * Route: /regulatory/registrations
 */
import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { PageHero } from '@edms/design-system';
import styles from './Regulatory.module.css';
import {
  useRegistrationsQuery, useCreateRegistration, STAGE_LABEL, CATEGORY_LABEL, type NewRegistrationInput,
} from './useRegulatory';

function fmtDate(iso: string | null | undefined) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: '2-digit' }); }
  catch { return String(iso); }
}
function stageClass(s: string) {
  if (s === 'APPROVED' || s === 'LAUNCHED') return styles.stageDone;
  if (s === 'DEFICIENCY_RESPONSE' || s === 'WITHDRAWN') return styles.stageWarn;
  return styles.stageActive;
}

export function RegistrationTracker() {
  const navigate = useNavigate();
  const [stage, setStage] = useState('');
  const [category, setCategory] = useState('');
  const q = useRegistrationsQuery({ stage: stage || undefined, category: category || undefined });
  const create = useCreateRegistration();

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<NewRegistrationInput>({ productName: '', category: 'PHARMA', market: 'Saudi Arabia', authority: 'SFDA' });
  const [err, setErr] = useState<string | null>(null);

  const items = q.data?.items ?? [];
  const stats = q.data?.stats;

  async function submit() {
    setErr(null);
    if (!form.productName.trim()) { setErr('Product name is required.'); return; }
    try {
      const created = await create.mutateAsync(form);
      setOpen(false);
      setForm({ productName: '', category: 'PHARMA', market: 'Saudi Arabia', authority: 'SFDA' });
      navigate({ to: '/regulatory/registrations/$id', params: { id: created.registrationId } });
    } catch (e: unknown) { setErr(e instanceof Error ? e.message : 'Could not start the registration.'); }
  }

  return (
    <main className={styles.page}>
      <PageHero
        eyebrow="Regulatory Affairs · RA-PLM"
        title="Registration Tracker"
        subtitle="New product registrations through the authority workflow — Concept → Dossier → Submission → Review → Approval → Launch."
        actions={<button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => { setOpen(true); setErr(null); }}>New registration</button>}
      />

      {stats && (
        <div className={styles.kpiRow}>
          <div className={styles.kpi}><span className={styles.kpiNum}>{stats.in_progress}</span><span className={styles.kpiLabel}>In progress</span></div>
          <div className={styles.kpi}><span className={styles.kpiNum}>{stats.in_review}</span><span className={styles.kpiLabel}>Authority review</span></div>
          <div className={`${styles.kpi} ${stats.deficiency > 0 ? styles.kpiWarn : ''}`}><span className={styles.kpiNum}>{stats.deficiency}</span><span className={styles.kpiLabel}>Deficiency</span></div>
          <div className={styles.kpi}><span className={styles.kpiNum}>{stats.approved}</span><span className={styles.kpiLabel}>Approved</span></div>
          <div className={`${styles.kpi} ${stats.overdue > 0 ? styles.kpiAlert : ''}`}><span className={styles.kpiNum}>{stats.overdue}</span><span className={styles.kpiLabel}>Overdue</span></div>
        </div>
      )}

      <div className={styles.filters}>
        <select aria-label="Stage filter" value={stage} onChange={(e) => setStage(e.target.value)} className={styles.select}>
          <option value="">All stages</option>
          {Object.keys(STAGE_LABEL).map((s) => <option key={s} value={s}>{STAGE_LABEL[s]}</option>)}
        </select>
        <select aria-label="Category filter" value={category} onChange={(e) => setCategory(e.target.value)} className={styles.select}>
          <option value="">All categories</option><option value="PHARMA">Pharmaceutical</option><option value="NUTRA">Nutraceutical</option><option value="MD">Medical Device</option>
        </select>
        <span className={styles.resultCount}>{items.length} registration{items.length === 1 ? '' : 's'}</span>
      </div>

      {q.isLoading ? (
        <div className={styles.empty}>Loading registrations…</div>
      ) : items.length === 0 ? (
        <div className={styles.empty}>No registrations match the current filters.</div>
      ) : (
        <table className={styles.table}>
          <thead><tr><th>Registration</th><th>Product</th><th>Category</th><th>Authority</th><th>Stage</th><th>Target approval</th></tr></thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.registrationId} className={styles.row} onClick={() => navigate({ to: '/regulatory/registrations/$id', params: { id: r.registrationId } })}>
                <td className={styles.mono}>{r.registrationCode}</td>
                <td>{r.productName}{r.strength ? <span className={styles.sub}> · {r.strength}</span> : ''}</td>
                <td>{CATEGORY_LABEL[r.category] ?? r.category}</td>
                <td>{r.authority}</td>
                <td><span className={`${styles.pill} ${stageClass(r.stage)}`}>{STAGE_LABEL[r.stage] ?? r.stage}</span></td>
                <td>{fmtDate(r.targetApprovalDate)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {open && (
        <div className={styles.modalBackdrop} role="dialog" aria-modal aria-label="New registration">
          <div className={styles.modal}>
            <h2 className={styles.modalTitle}>Start a registration</h2>
            {err && <div className={styles.err} role="alert">{err}</div>}
            <label className={styles.field}><span>Product name *</span>
              <input type="text" value={form.productName} onChange={(e) => setForm({ ...form, productName: e.target.value })} placeholder="e.g. Atorvastatin 20 mg Tablets" /></label>
            <div className={styles.field2}>
              <label className={styles.field}><span>Category</span>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                  <option value="PHARMA">Pharmaceutical</option><option value="NUTRA">Nutraceutical</option><option value="MD">Medical Device</option>
                </select></label>
              <label className={styles.field}><span>Authority</span>
                <input type="text" value={form.authority ?? ''} onChange={(e) => setForm({ ...form, authority: e.target.value })} /></label>
            </div>
            <div className={styles.field2}>
              <label className={styles.field}><span>Dosage form</span>
                <input type="text" value={form.dosageForm ?? ''} onChange={(e) => setForm({ ...form, dosageForm: e.target.value })} /></label>
              <label className={styles.field}><span>Strength</span>
                <input type="text" value={form.strength ?? ''} onChange={(e) => setForm({ ...form, strength: e.target.value })} /></label>
            </div>
            <label className={styles.field}><span>Target approval date</span>
              <input type="date" value={form.targetApprovalDate ?? ''} onChange={(e) => setForm({ ...form, targetApprovalDate: e.target.value })} /></label>
            <div className={styles.modalActions}>
              <button type="button" className={`${styles.btn} ${styles.btnNeutral}`} onClick={() => setOpen(false)}>Cancel</button>
              <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => void submit()} disabled={create.isPending}>
                {create.isPending ? 'Starting…' : 'Start registration'}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
